<template>
  <div class="trend-indicator" :class="trendClass">
    <v-icon 
      :icon="trendIcon" 
      :color="trendColor"
      size="small"
    />
    
    <span v-if="change !== undefined" class="trend-value">
      {{ formatChange(change) }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

// 📝 Props
interface Props {
  trend: 'up' | 'down' | 'stable'
  change?: number
  showValue?: boolean
  size?: 'small' | 'medium' | 'large'
}

const props = withDefaults(defineProps<Props>(), {
  showValue: true,
  size: 'medium'
})

// 📊 Computed Properties
const trendClass = computed(() => [
  `trend-indicator--${props.trend}`,
  `trend-indicator--${props.size}`
])

const trendIcon = computed(() => {
  switch (props.trend) {
    case 'up':
      return 'mdi-trending-up'
    case 'down':
      return 'mdi-trending-down'
    case 'stable':
    default:
      return 'mdi-minus'
  }
})

const trendColor = computed(() => {
  switch (props.trend) {
    case 'up':
      return '#4caf50'
    case 'down':
      return '#f44336'
    case 'stable':
    default:
      return '#757575'
  }
})

// 🔧 Helper Functions
const formatChange = (value: number): string => {
  const absoluteValue = Math.abs(value)
  const sign = value >= 0 ? '+' : '-'
  
  if (absoluteValue >= 100) {
    return `${sign}${absoluteValue.toFixed(0)}%`
  } else if (absoluteValue >= 10) {
    return `${sign}${absoluteValue.toFixed(1)}%`
  } else {
    return `${sign}${absoluteValue.toFixed(2)}%`
  }
}
</script>

<style scoped>
.trend-indicator {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.75rem;
  transition: all 0.2s ease;
}

.trend-indicator--small {
  padding: 1px 4px;
  font-size: 0.7rem;
  gap: 2px;
}

.trend-indicator--medium {
  padding: 2px 6px;
  font-size: 0.75rem;
  gap: 4px;
}

.trend-indicator--large {
  padding: 4px 8px;
  font-size: 0.8rem;
  gap: 6px;
}

.trend-indicator--up {
  background-color: rgba(76, 175, 80, 0.1);
  color: #4caf50;
  border: 1px solid rgba(76, 175, 80, 0.2);
}

.trend-indicator--down {
  background-color: rgba(244, 67, 54, 0.1);
  color: #f44336;
  border: 1px solid rgba(244, 67, 54, 0.2);
}

.trend-indicator--stable {
  background-color: rgba(117, 117, 117, 0.1);
  color: #757575;
  border: 1px solid rgba(117, 117, 117, 0.2);
}

.trend-value {
  font-variant-numeric: tabular-nums;
  line-height: 1;
}

.trend-indicator:hover {
  transform: scale(1.05);
}
</style>
